/**
 * The transport seam: the only file that calls `HttpTransport.request`, so a
 * rejection becomes an `AccountApiError` in exactly one place, and every body
 * is parsed through the binding's wire schema (`@dealr/api-common`).
 */

import { readConfirmPrompt, withConfirmAnswers } from '@dealr/transport';
import type { ConfirmAnswers, ConfirmOutcome, ConfirmPrompt, HttpRequest, HttpTransport } from '@dealr/transport';
import { endpointLabel, parseWire } from '@dealr/api-common';
import type { WireSchema } from '@dealr/api-common';
import { toAccountApiError } from './errors';

export function send<T>(transport: HttpTransport, req: HttpRequest, schema: WireSchema): Promise<T> {
  return transport.request(req).then(
    (res) => parseWire(schema, res.data, endpointLabel(req)) as T,
    (rejection) => {
      throw toAccountApiError(rejection);
    },
  );
}

/**
 * `send` for a handler that can raise an `x-client-confirm-dialog` prompt:
 * resolves `{kind: 'ok', value}` or `{kind: 'confirm', prompt, resume}`, and
 * `resume(choiceKey)` replays the original request with the accumulated
 * answers (the `@dealr/api-service` `sendConfirmable` contract). A web
 * island's transport answers prompts itself, so there `'confirm'` means the
 * operator declined.
 */
export function sendConfirmable<T>(
  transport: HttpTransport,
  req: HttpRequest,
  schema: WireSchema,
  answers: ConfirmAnswers = {},
): Promise<ConfirmOutcome<T>> {
  return transport.request(withConfirmAnswers(req, answers)).then<ConfirmOutcome<T>, ConfirmOutcome<T>>(
    (res) => ({ kind: 'ok', value: parseWire(schema, res.data, endpointLabel(req)) as T }),
    (rejection) => {
      const prompt = readConfirmPrompt(rejection);
      if (!prompt) throw toAccountApiError(rejection);
      return {
        kind: 'confirm',
        prompt,
        resume: (choiceKey: string) => sendConfirmable<T>(transport, req, schema, { ...answers, [prompt.type]: choiceKey }),
      };
    },
  );
}

/** Answers an `x-client-confirm-dialog` prompt: true takes its first option, false gives up. */
export type ConfirmPort = (prompt: ConfirmPrompt) => boolean | Promise<boolean>;

const MAX_CONFIRM_PROMPTS = 3;

/**
 * Settles a `sendConfirmable` outcome for a pre-auth caller with no prompt UI
 * of its own (the session-limit question on the login MFA step): `confirm`
 * answers each prompt with its first option, as the login page does for
 * `POST /api/login`. A declined or unanswerable prompt rejects with the
 * original 400.
 */
export async function answerConfirms<T>(outcome: Promise<ConfirmOutcome<T>>, confirm: ConfirmPort | undefined): Promise<T> {
  let current = await outcome;
  for (let asked = 0; current.kind === 'confirm'; asked++) {
    const prompt = current.prompt;
    const choice = prompt.type && prompt.buttons[0] ? prompt.buttons[0].key : null;
    if (!confirm || !choice || asked >= MAX_CONFIRM_PROMPTS || !(await confirm(prompt))) {
      throw toAccountApiError({ status: 400, data: { type: prompt.type, options: { message: prompt.message, buttons: prompt.buttons } } });
    }
    current = await current.resume(choice);
  }
  return current.value;
}
